import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHero from '../components/common/PageHero';
import FileUpload from '../components/common/FileUpload';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  Calendar as CalendarIcon,
  Plus,
  Edit,
  Trash2,
  Users,
  MapPin,
  Clock,
  DollarSign,
  Award,
  Search,
  Save,
  X,
  ArrowLeft
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const EventsManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '09:00',
    end_date: '',
    location: '',
    venue_details: '',
    capacity: 100,
    registration_deadline: '',
    event_type: 'general',
    cpd_points: 0,
    registration_fee: 0,
    image_url: '',
    file_url: ''
  });

  const eventTypes = ['general', 'conference', 'workshop', 'seminar', 'social', 'webinar'];

  useEffect(() => {
    fetchEvents();
  }, [statusFilter, typeFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (typeFilter !== 'all') params.event_type = typeFilter;
      
      const response = await axios.get(`${backendUrl}/api/events`, { params });
      setEvents(response.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch events',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.event_date || !formData.location) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const payload = { ...formData };
      if (!payload.end_date) delete payload.end_date;
      if (!payload.registration_deadline) delete payload.registration_deadline;
      if (!payload.image_url) delete payload.image_url;
      if (!payload.venue_details) delete payload.venue_details;

      if (editingEvent) {
        // Update existing event
        await axios.put(`${backendUrl}/api/admin/events/${editingEvent.id}`, payload);
        toast({
          title: 'Event Updated',
          description: 'Event has been updated successfully'
        });
      } else {
        // Create new event
        await axios.post(`${backendUrl}/api/admin/events`, payload);
        toast({
          title: 'Event Created',
          description: 'Event has been created successfully'
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save event',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_date: event.event_date ? new Date(event.event_date).toISOString().split('T')[0] : '',
      event_time: event.event_time || '09:00',
      end_date: event.end_date ? new Date(event.end_date).toISOString().split('T')[0] : '',
      location: event.location,
      venue_details: event.venue_details || '',
      capacity: event.capacity,
      registration_deadline: event.registration_deadline ? new Date(event.registration_deadline).toISOString().split('T')[0] : '',
      event_type: event.event_type,
      cpd_points: event.cpd_points,
      registration_fee: event.registration_fee,
      image_url: event.image_url || '',
      file_url: event.file_url || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (eventId, title) => {
    if (!window.confirm(`Are you sure you want to archive "${title}"? It can be restored later.`)) return;
    
    try {
      await axios.delete(`${backendUrl}/api/admin/events/${eventId}`);
      toast({
        title: 'Event Archived',
        description: 'Event has been archived'
      });
      fetchEvents();
    } catch (error) {
      toast({
        title: 'Delete Failed',
        description: error.response?.data?.detail || 'Failed to delete event',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_date: '',
      event_time: '09:00',
      end_date: '',
      location: '',
      venue_details: '',
      capacity: 100,
      registration_deadline: '',
      event_type: 'general',
      cpd_points: 0,
      registration_fee: 0,
      image_url: '',
      file_url: ''
    });
    setEditingEvent(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'ongoing': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEvents = events.filter(event => 
    event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    event.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="EVENTS MANAGEMENT" />
      
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>
        {/* Header Actions */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex items-center gap-4 flex-1 w-full md:w-auto">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                <Plus size={18} className="mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingEvent ? 'Edit Event' : 'Create New Event'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter event title"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Event description"
                    rows={4}
                    className="mt-2"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event_date">Event Date *</Label>
                    <Input
                      id="event_date"
                      name="event_date"
                      type="date"
                      value={formData.event_date}
                      onChange={handleChange}
                      className="mt-2"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="event_time">Event Time *</Label>
                    <Input
                      id="event_time"
                      name="event_time"
                      type="time"
                      value={formData.event_time}
                      onChange={handleChange}
                      className="mt-2"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="end_date">End Date (Optional)</Label>
                    <Input
                      id="end_date"
                      name="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={handleChange}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="registration_deadline">Registration Deadline</Label>
                    <Input
                      id="registration_deadline"
                      name="registration_deadline"
                      type="date"
                      value={formData.registration_deadline}
                      onChange={handleChange}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Event location"
                    className="mt-2"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="venue_details">Venue Details</Label>
                  <Input
                    id="venue_details"
                    name="venue_details"
                    value={formData.venue_details}
                    onChange={handleChange}
                    placeholder="Hall, room number, floor, etc."
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event_type">Event Type</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {eventTypes.map(type => (
                          <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="capacity">Capacity</Label>
                    <Input
                      id="capacity"
                      name="capacity"
                      type="number"
                      value={formData.capacity}
                      onChange={handleChange}
                      className="mt-2"
                      min="1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cpd_points">CPD Points</Label>
                    <Input
                      id="cpd_points"
                      name="cpd_points"
                      type="number"
                      step="0.5"
                      value={formData.cpd_points}
                      onChange={handleChange}
                      className="mt-2"
                      min="0"
                    />
                  </div>

                  <div>
                    <Label htmlFor="registration_fee">Registration Fee (ZAR)</Label>
                    <Input
                      id="registration_fee"
                      name="registration_fee"
                      type="number"
                      step="0.01"
                      value={formData.registration_fee}
                      onChange={handleChange}
                      className="mt-2"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://example.com/event-image.jpg"
                    className="mt-2"
                  />
                </div>

                <div>
                  <FileUpload
                    folder="events"
                    adminOnly={true}
                    label="Event Document (schedule, program, etc.)"
                    currentUrl={formData.file_url}
                    onUpload={(url) => setFormData(prev => ({ ...prev, file_url: url }))}
                    onClear={() => setFormData(prev => ({ ...prev, file_url: '' }))}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsDialogOpen(false);
                      resetForm();
                    }}
                  >
                    <X size={16} className="mr-2" />
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                    <Save size={16} className="mr-2" />
                    {editingEvent ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Filter by Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Filter by Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {eventTypes.map(type => (
                      <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button onClick={fetchEvents} className="w-full bg-[#2c3e7d]">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events List */}
        <Card>
          <CardHeader>
            <CardTitle>Events ({filteredEvents.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-4 text-gray-600">No events found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEvents.map(event => (
                  <div
                    key={event.id}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{event.title}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(event.status)}`}>
                          {event.status}
                        </span>
                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 capitalize">
                          {event.event_type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{event.description.substring(0, 100)}...</p>
                      <div className="text-xs text-gray-500 flex flex-wrap gap-3">
                        <span className="flex items-center gap-1">
                          <CalendarIcon size={12} />
                          {new Date(event.event_date).toLocaleDateString()} @ {event.event_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin size={12} />
                          {event.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={12} />
                          {event.registered_count}/{event.capacity}
                        </span>
                        {event.cpd_points > 0 && (
                          <span className="flex items-center gap-1">
                            <Award size={12} />
                            {event.cpd_points} CPD Points
                          </span>
                        )}
                        {event.registration_fee > 0 && (
                          <span className="flex items-center gap-1">
                            <DollarSign size={12} />
                            R{event.registration_fee}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(event)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(event.id, event.title)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EventsManagement;